import type { Speaker, Track } from "../event-experience/schemas";

import { cleanValue } from "./announcement-placeholders";
import type { AttendeeRecord } from "./use-attendee-records";

/**
 * A track champion (TNG-95) is a link to a roster attendee or a speaker. Its
 * name is looked up when it is shown, the way the backend looks it up when an
 * announcement goes out (firestore_announcement_recipients.py), so both say
 * the same thing about the same track.
 */

/** Whose roster row still stands for somebody; attendee_projection.py. */
const ROSTER_STATUSES = new Set(["pending", "confirmed"]);

export function isOnRoster(attendee: Pick<AttendeeRecord, "status">): boolean {
  return ROSTER_STATUSES.has(attendee.status ?? "");
}

export type ChampionLink =
  { kind: "attendee"; id: string } | { kind: "speaker"; id: string };

/** The fields a track keeps its champions in: the first, then the second. */
export type TrackChampionFields = Pick<
  Track,
  | "championAttendeeId"
  | "championSpeakerId"
  | "secondChampionAttendeeId"
  | "secondChampionSpeakerId"
>;

function linkOf(
  attendeeId: string | undefined,
  speakerId: string | undefined,
): ChampionLink | null {
  if (attendeeId) return { kind: "attendee", id: attendeeId };
  if (speakerId) return { kind: "speaker", id: speakerId };
  return null;
}

/** The first champion's link, or null. */
export function championLink(
  track: Pick<Track, "championAttendeeId" | "championSpeakerId">,
): ChampionLink | null {
  return linkOf(track.championAttendeeId, track.championSpeakerId);
}

/** The second champion's link, or null. */
export function secondChampionLink(
  track: Pick<Track, "secondChampionAttendeeId" | "secondChampionSpeakerId">,
): ChampionLink | null {
  return linkOf(track.secondChampionAttendeeId, track.secondChampionSpeakerId);
}

/**
 * Both champion links, first then second, each at most once: a track that
 * names the same person twice has one champion.
 */
export function championLinks(track: TrackChampionFields): ChampionLink[] {
  const links: ChampionLink[] = [];
  for (const link of [championLink(track), secondChampionLink(track)]) {
    if (link && !links.some((l) => l.kind === link.kind && l.id === link.id))
      links.push(link);
  }
  return links;
}

/** One link's current name, or null when it points at nobody in the event. */
export function linkName(
  link: ChampionLink,
  attendees: ReadonlyMap<string, AttendeeRecord>,
  speakers: ReadonlyMap<string, Speaker>,
): string | null {
  if (link.kind === "attendee") {
    const row = attendees.get(link.id);
    return row && isOnRoster(row) ? cleanValue(row.name) : null;
  }
  return cleanValue(speakers.get(link.id)?.name);
}

/**
 * The names of the track's champions as they read now, first then second.
 * A link that points at someone no longer in the event (row deleted or
 * withdrawn, speaker deleted) is skipped.
 */
export function championNames(
  track: TrackChampionFields,
  attendees: ReadonlyMap<string, AttendeeRecord>,
  speakers: ReadonlyMap<string, Speaker>,
): string[] {
  return championLinks(track)
    .map((link) => linkName(link, attendees, speakers))
    .filter((name): name is string => name !== null);
}

/**
 * What {champion} says: both names joined with " and ", one name alone, or
 * null when the track has none or every link is stale. A personalised
 * announcement then says "TBA" or its fallback. The backend joins the same
 * way (firestore_announcement_recipients.py).
 */
export function championName(
  track: TrackChampionFields,
  attendees: ReadonlyMap<string, AttendeeRecord>,
  speakers: ReadonlyMap<string, Speaker>,
): string | null {
  const names = championNames(track, attendees, speakers);
  return names.length > 0 ? names.join(" and ") : null;
}
